import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { Box, FormControl } from "@mui/material";
import {
  StyledSelect,
  SelectionPanelContainer,
  surfaceSelectionDropdownPaperStyles,
} from "../Select/Select.styled";
import type { SurfaceSelectionTab } from "../../utils/treatAreaCapabilities";
import SurfaceSelectionPanel from "./SurfaceSelectionPanel";

interface SurfaceSelectionSelectProps {
  value?: string[];
  onChange?: (
    value: string[],
    meta?: { clearTeeth?: boolean },
  ) => void;
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  name?: string;
  allowedTabs?: SurfaceSelectionTab[];
  initialTab?: SurfaceSelectionTab;
}

export interface SurfaceSelectionSelectHandle {
  open: () => void;
  close: () => void;
}

const SurfaceSelectionSelect = forwardRef<
  SurfaceSelectionSelectHandle,
  SurfaceSelectionSelectProps
>(function SurfaceSelectionSelect(
  {
    value = [],
    onChange,
    hideTrigger = false,
    open: controlledOpen,
    onOpenChange,
    name,
    allowedTabs,
    initialTab,
  },
  ref,
) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpenControlled = controlledOpen !== undefined;
  const open = isOpenControlled ? controlledOpen : internalOpen;

  const setOpenState = useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isOpenControlled, onOpenChange],
  );

  const handleOpen = () => {
    setOpenState(true);
  };

  const handleClose = useCallback(() => {
    setOpenState(false);
  }, [setOpenState]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpenState(true),
      close: () => handleClose(),
    }),
    [handleClose, setOpenState],
  );

  return (
    <Box
      sx={{
        width: hideTrigger ? "100%" : "100%",
        height: hideTrigger ? "100%" : undefined,
        position: hideTrigger ? "absolute" : "relative",
        inset: hideTrigger ? 0 : undefined,
        opacity: hideTrigger ? 0 : 1,
        overflow: hideTrigger ? "hidden" : "visible",
        pointerEvents: hideTrigger ? "none" : "auto",
      }}
    >
      <FormControl fullWidth sx={{ height: "100%", width: "100%" }}>
        <StyledSelect
          value={value}
          onChange={() => {}}
          multiple
          displayEmpty
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          name={name}
          renderValue={() => null}
          IconComponent={() => null}
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            height: hideTrigger ? "100%" : undefined,
            "& .MuiSelect-select": {
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            },
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              sx: surfaceSelectionDropdownPaperStyles,
            },
          }}
        >
          <SelectionPanelContainer>
            <SurfaceSelectionPanel
              key={`${(allowedTabs ?? []).join(",")}-${initialTab ?? ""}`}
              value={value}
              onChange={(nextValue, meta) => onChange?.(nextValue, meta)}
              allowedTabs={allowedTabs}
              initialTab={initialTab}
            />
          </SelectionPanelContainer>
        </StyledSelect>
      </FormControl>
    </Box>
  );
});

export default SurfaceSelectionSelect;
